import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCircleInfo,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../components/Navbar";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import { formatDisplayDate } from "../utils/dateFormat";
import PaginationFooter from "../components/PaginationFooter";
import AppTooltip from "../components/AppTooltip";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import {
  FEE_PROOF_ACCEPT,
  isSafeFeeProofDisplayUrl,
  validateFeeProofFile,
} from "../utils/feeProofUpload";
import {
  downloadCreditConfirmationForm,
  downloadPayoutReceivingForm,
} from "../utils/teacherAccountFormPdf";
import * as teacherAccountApi from "../api/teacherAccount";

const emptyForm = {
  entryType: "payout",
  amount: "",
  entryDate: "",
  description: "",
};

const filterControlStyle = {
  width: "165px",
  height: "38px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: "#fff",
  fontSize: "12px",
};

const TYPE_META = {
  fee_earning: {
    label: "Fee earnings",
    hint: "Credits from verified student payments",
  },
  fee_reversal: {
    label: "Reversals",
    hint: "Debits from updates, drops, or transfers out",
  },
  transfer_in: {
    label: "Transfers in",
    hint: "Credits received from course transfers",
  },
  payout: {
    label: "Payouts",
    hint: "Money paid out to the teacher",
  },
  manual_credit: {
    label: "Manual adds",
    hint: "Admin credits added to the account",
  },
  legacy_payment: {
    label: "Legacy",
    hint: "Migrated opening balances from the old system",
  },
};

const PRIMARY_CATEGORY_TYPES = [
  "fee_earning",
  "fee_reversal",
  "transfer_in",
  "payout",
  "manual_credit",
];

const EDITABLE_TYPES = new Set(["payout", "manual_credit"]);

const entryTypeLabel = (entryType) => {
  if (TYPE_META[entryType]) return TYPE_META[entryType].label;
  if (entryType === "payout_reversal") return "Payout reversal";
  if (
    entryType === "manual_credit_reversal" ||
    entryType === "manual_debit"
  ) {
    return "Manual add reversal";
  }
  if (entryType === "legacy_payment") return "Legacy";
  return entryType;
};

const getSoftEditParentType = (entry, entriesById) => {
  const meta = entry.meta || {};
  if (meta.reversesEntryType) return meta.reversesEntryType;
  if (!meta.reversesEntryId) return null;
  return entriesById.get(Number(meta.reversesEntryId))?.entryType || null;
};

const resolveRowTypeLabel = (entry, entriesById) => {
  const parentType = getSoftEditParentType(entry, entriesById);
  if (parentType === "payout") return "Payout reversal";
  if (parentType === "manual_credit") return "Manual add reversal";
  return entryTypeLabel(entry.entryType);
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const formatMoney = (value) =>
  `PKR ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

export default function AdminTeacherAccount() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_teachers");
  const { hasPermission: canEdit } = useViewPermission("edit_teachers");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({
    totalCredit: 0,
    totalDebit: 0,
    payableBalance: 0,
  });

  const [showBalance, setShowBalance] = useState(false);
  const [showCredit, setShowCredit] = useState(false);
  const [showDebit, setShowDebit] = useState(false);

  const [filters, setFilters] = useState({
    entryType: "",
    direction: "",
    fromDate: "",
    toDate: "",
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [receivingFormFile, setReceivingFormFile] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewingReceivingUrl, setViewingReceivingUrl] = useState("");

  const todayDate = () => new Date().toISOString().slice(0, 10);

  const isTodayFilterActive =
    appliedDateRange.fromDate === todayDate() &&
    appliedDateRange.toDate === todayDate() &&
    !filters.entryType &&
    !filters.direction;

  const fetchAccount = async (
    pageArg = page,
    filterOverrides = {},
    isInitial = false,
  ) => {
    if (!isInitial) setRefreshing(true);
    try {
      const entryType = filterOverrides.entryType ?? filters.entryType;
      const direction = filterOverrides.direction ?? filters.direction;
      const fromDate = filterOverrides.fromDate ?? appliedDateRange.fromDate;
      const toDate = filterOverrides.toDate ?? appliedDateRange.toDate;

      const [headerRes, listRes] = await Promise.all([
        teacherAccountApi.getTeacher(id),
        teacherAccountApi.list({
          page: pageArg,
          limit,
          filters: {
            teacherId: id,
            entryType,
            direction,
            fromDate,
            toDate,
          },
        }),
      ]);
      setTeacher(headerRes.data.teacher);
      const normalized = normalizeListResponse(listRes.data, pageArg, limit);
      setEntries(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
      setSummary({
        totalCredit: Number(headerRes.data.totalCredit || 0),
        totalDebit: Number(headerRes.data.totalDebit || 0),
        payableBalance: Number(headerRes.data.payableBalance || 0),
      });
    } catch (err) {
      console.error("Error loading teacher account:", err);
      notificationCtx.show({
        message: "Failed to load teacher account.",
        stat: "error",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!hasPermission || !id) return;
    fetchAccount(1, {}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, id]);

  const statementRows = useMemo(() => {
    const entriesById = new Map(
      entries.map((entry) => [Number(entry.id), entry]),
    );

    const chronological = [...entries].sort((a, b) => {
      const byDate = String(a.entryDate).localeCompare(String(b.entryDate));
      if (byDate !== 0) return byDate;
      return Number(a.id) - Number(b.id);
    });

    let running = 0;
    const withBalances = chronological.map((entry) => {
      const amount = Number(entry.amount || 0);
      const openingBalance = running;
      const delta = entry.direction === "credit" ? amount : -amount;
      running = roundMoney(running + delta);
      return {
        ...entry,
        openingBalance,
        closingBalance: running,
        signedAmount: delta,
        typeLabel: resolveRowTypeLabel(entry, entriesById),
      };
    });

    return [...withBalances].sort((a, b) => {
      const byDate = String(b.entryDate).localeCompare(String(a.entryDate));
      if (byDate !== 0) return byDate;
      return Number(b.id) - Number(a.id);
    });
  }, [entries]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchAccount(newPage);
    }
  };

  const resetFilters = () => {
    setFilters({
      entryType: "",
      direction: "",
      fromDate: "",
      toDate: "",
    });
    setAppliedDateRange({ fromDate: "", toDate: "" });
    fetchAccount(1, { entryType: "", direction: "", fromDate: "", toDate: "" });
  };

  const applyDateFilter = () => {
    if (!filters.fromDate || !filters.toDate) return;
    if (new Date(filters.fromDate) > new Date(filters.toDate)) {
      notificationCtx.show({
        message: "Start date cannot be after end date.",
      });
      return;
    }
    setAppliedDateRange({
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    });
    fetchAccount(1, { fromDate: filters.fromDate, toDate: filters.toDate });
  };

  const toggleToday = () => {
    if (isTodayFilterActive) {
      resetFilters();
      return;
    }
    const today = todayDate();
    setFilters({
      entryType: "",
      direction: "",
      fromDate: today,
      toDate: today,
    });
    setAppliedDateRange({ fromDate: today, toDate: today });
    fetchAccount(1, {
      entryType: "",
      direction: "",
      fromDate: today,
      toDate: today,
    });
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyForm);
    setEditingEntry(null);
    setReceivingFormFile(null);
  };

  const openAdd = (entryType) => {
    setFormMode("add");
    setEditingEntry(null);
    setReceivingFormFile(null);
    setForm({
      entryType,
      amount: "",
      entryDate: todayDate(),
      description: "",
    });
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setFormMode("edit");
    setEditingEntry(entry);
    setReceivingFormFile(null);
    setForm({
      entryType: entry.entryType,
      amount: String(entry.amount),
      entryDate: String(entry.entryDate).slice(0, 10),
      description: entry.description || "",
    });
    setShowForm(true);
  };

  const handleDownloadReceivingTemplate = async () => {
    if (downloadingTemplate) return;
    setDownloadingTemplate(true);
    try {
      const shared = {
        teacherName: teacher?.name || "",
        amount: form.amount,
        entryDate: form.entryDate,
        description: form.description,
      };
      const adminName = user?.name || user?.nickname || "";
      if (form.entryType === "manual_credit") {
        await downloadCreditConfirmationForm({
          ...shared,
          adminName,
        });
      } else {
        await downloadPayoutReceivingForm({
          ...shared,
          adminName,
        });
      }
    } catch (err) {
      console.error("Error generating confirmation form:", err);
      notificationCtx.show({
        message: "Failed to download confirmation form template.",
        stat: "error",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleReceivingFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setReceivingFormFile(null);
      return;
    }
    const error = validateFeeProofFile(file);
    if (error) {
      notificationCtx.show({ message: error });
      e.target.value = "";
      setReceivingFormFile(null);
      return;
    }
    setReceivingFormFile(file);
  };

  const saveForm = async () => {
    if (!canEdit) return;
    if (saving) return;
    if (!form.amount || !form.entryDate || !form.description.trim()) {
      notificationCtx.show({
        message: "Amount, date, and description are required.",
      });
      return;
    }
    const needsConfirmationImage =
      formMode === "add" &&
      (form.entryType === "payout" || form.entryType === "manual_credit");
    if (needsConfirmationImage) {
      const fileError = validateFeeProofFile(receivingFormFile);
      if (fileError) {
        notificationCtx.show({
          message:
            fileError === "Please choose an image file."
              ? form.entryType === "payout"
                ? "Signed receiving form image is required for payouts."
                : "Signed credit confirmation form image is required for credits."
              : fileError,
        });
        return;
      }
    }
    setSaving(true);
    try {
      if (formMode === "edit" && editingEntry) {
        await teacherAccountApi.softEdit(editingEntry.id, {
          amount: form.amount,
          entryDate: form.entryDate,
          description: form.description.trim(),
        });
        notificationCtx.show({
          message: "Entry updated via reversal + replacement.",
          stat: "success",
        });
      } else if (needsConfirmationImage) {
        const formData = new FormData();
        formData.append("teacherId", String(Number(id)));
        formData.append("entryType", form.entryType);
        formData.append("amount", String(form.amount));
        formData.append("entryDate", form.entryDate);
        formData.append("description", form.description.trim());
        formData.append("file", receivingFormFile);
        await teacherAccountApi.create(formData);
        notificationCtx.show({
          message: "Account entry recorded.",
          stat: "success",
        });
      } else {
        await teacherAccountApi.create({
          teacherId: Number(id),
          entryType: form.entryType,
          amount: form.amount,
          entryDate: form.entryDate,
          description: form.description.trim(),
        });
        notificationCtx.show({
          message: "Account entry recorded.",
          stat: "success",
        });
      }
      closeForm();
      await fetchAccount();
    } catch (err) {
      console.error("Error saving teacher account entry:", err);
      notificationCtx.show({
        message: err.response?.data?.message || "Failed to save entry.",
        stat: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const maskedOrMoney = (visible, value) =>
    visible ? formatMoney(value) : "••••••";

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  const balanceTone = summary.payableBalance >= 0 ? "#2f6b4f" : "#8a3b3b";

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            margin: "100px 50px 0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "26px 30px",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center flex-wrap gap-3"
              style={{ marginBottom: "16px" }}
            >
              <div className="d-flex align-items-center gap-3 flex-grow-1">
                <AppTooltip label="Back to teacher">
                <button
                  className="btn"
                  onClick={() =>
                    navigate(`/admin/teachers/${id}`, { replace: true })
                  }
                  style={{
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    color: "#403b55",
                    flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    style={{ fontSize: "13px" }}
                  />
                </button>
                </AppTooltip>
                {teacher?.picture ? (
                  <img
                    src={teacher.picture}
                    alt={teacher.name}
                    style={{
                      width: "92px",
                      height: "92px",
                      borderRadius: "18px",
                      objectFit: "cover",
                      border: "3px solid rgba(255,255,255,0.75)",
                      boxShadow: "0 6px 18px rgba(48,45,69,0.12)",
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <div style={{ minWidth: 0, marginRight: "8px" }}>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#625d75",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Teacher account
                  </div>
                  <h1
                    className="mb-1"
                    style={{
                      fontWeight: "700",
                      color: "#302d45",
                      fontSize: "26px",
                    }}
                  >
                    {teacher?.name || "Teacher"}
                  </h1>
                  <small style={{ color: "#625d75" }}>
                    Commission {teacher?.commission ?? 0}%
                  </small>
                </div>

                <div className="d-flex align-items-stretch gap-2 flex-wrap flex-grow-1 justify-content-end">
                  {[
                    {
                      key: "balance",
                      label: "Payable balance",
                      value: summary.payableBalance,
                      visible: showBalance,
                      toggle: () => setShowBalance((v) => !v),
                      color: balanceTone,
                    },
                    {
                      key: "credit",
                      label: "Total credit",
                      value: summary.totalCredit,
                      visible: showCredit,
                      toggle: () => setShowCredit((v) => !v),
                      color: "#2f6b4f",
                    },
                    {
                      key: "debit",
                      label: "Total debit",
                      value: summary.totalDebit,
                      visible: showDebit,
                      toggle: () => setShowDebit((v) => !v),
                      color: "#8a3b3b",
                    },
                  ].map((card) => (
                    <div
                      key={card.key}
                      style={{
                        backgroundColor: "rgba(255,255,255,0.72)",
                        borderRadius: "12px",
                        padding: "10px 12px",
                        width: "250px",
                        flexShrink: 0,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span
                          style={{
                            color: "#625d75",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          {card.label}
                        </span>
                        <AppTooltip
                          label={card.visible ? "Hide amount" : "Show amount"}
                        >
                        <button
                          type="button"
                          className="btn p-0"
                          onClick={card.toggle}
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "7px",
                            backgroundColor: "#ece8f7",
                            border: "none",
                            color: "#403b55",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={card.visible ? faEyeSlash : faEye}
                            style={{ fontSize: "11px" }}
                          />
                        </button>
                        </AppTooltip>
                      </div>
                      <div
                        style={{
                          color: card.color,
                          fontWeight: "800",
                          fontSize: "17px",
                          lineHeight: 1.15,
                          letterSpacing: card.visible ? "normal" : "1.5px",
                        }}
                      >
                        {maskedOrMoney(card.visible, card.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#514b65",
                  letterSpacing: "0.2px",
                  marginBottom: "8px",
                }}
              >
                ENTRY CATEGORIES
              </div>
              <div className="d-flex flex-wrap align-items-stretch justify-content-between gap-2">
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn text-start"
                    onClick={() => {
                      setFilters((prev) => ({ ...prev, entryType: "" }));
                      fetchAccount(1, { entryType: "" });
                    }}
                    style={{
                      minWidth: "130px",
                      backgroundColor:
                        filters.entryType === "" ? "#bff1db" : "#fff",
                      color:
                        filters.entryType === "" ? "#355c4a" : "#403b55",
                      border: "none",
                      borderRadius: "12px",
                      padding: "12px 14px",
                      fontWeight: "700",
                      fontSize: "13px",
                    }}
                  >
                    All activity
                  </button>
                  {PRIMARY_CATEGORY_TYPES.map((type) => {
                    const meta = TYPE_META[type];
                    if (!meta) return null;
                    const active = filters.entryType === type;
                    return (
                      <button
                        type="button"
                        key={type}
                        className="btn text-start"
                        onClick={() => {
                          const nextEntryType = active ? "" : type;
                          setFilters((prev) => ({
                            ...prev,
                            entryType: nextEntryType,
                          }));
                          fetchAccount(1, { entryType: nextEntryType });
                        }}
                        style={{
                          minWidth: "148px",
                          maxWidth: "180px",
                          backgroundColor: active ? "#bff1db" : "#fff",
                          color: active ? "#355c4a" : "#403b55",
                          border: "none",
                          borderRadius: "12px",
                          padding: "12px 14px",
                          position: "relative",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center gap-2">
                          <div style={{ fontWeight: "700", fontSize: "13px" }}>
                            {meta.label}
                          </div>
                          <AppTooltip label={meta.hint} wrap>
                          <span
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "18px",
                              height: "18px",
                              borderRadius: "50%",
                              backgroundColor: active
                                ? "rgba(53,92,74,0.12)"
                                : "#ece8f7",
                              color: active ? "#355c4a" : "#625d75",
                              flexShrink: 0,
                              cursor: "help",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              style={{ fontSize: "10px" }}
                            />
                          </span>
                          </AppTooltip>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    className="btn"
                    disabled={refreshing}
                    onClick={resetFilters}
                    style={{
                      height: "42px",
                      backgroundColor: "#fff",
                      border: "none",
                      color: "#625d75",
                      fontWeight: "600",
                      fontSize: "13px",
                      padding: "0 15px",
                      borderRadius: "10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Reset All
                  </button>
                  {canEdit && (
                    <>
                      <button
                        className="btn"
                        onClick={() => openAdd("payout")}
                        style={{
                          height: "42px",
                          backgroundColor: "#fff",
                          border: "none",
                          color: "#403b55",
                          fontWeight: "600",
                          fontSize: "13px",
                          padding: "0 15px",
                          borderRadius: "10px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        + Record Payout
                      </button>
                      <button
                        className="btn"
                        onClick={() => openAdd("manual_credit")}
                        style={{
                          height: "42px",
                          backgroundColor: "#fff",
                          border: "none",
                          color: "#403b55",
                          fontWeight: "600",
                          fontSize: "13px",
                          padding: "0 15px",
                          borderRadius: "10px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        + Add Credit
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <select
                  className="form-select"
                  value={filters.direction}
                  onChange={(e) => {
                    const nextDirection = e.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      direction: nextDirection,
                    }));
                    fetchAccount(1, { direction: nextDirection });
                  }}
                  style={filterControlStyle}
                >
                  <option value="">Credit & debit</option>
                  <option value="credit">Credit only</option>
                  <option value="debit">Debit only</option>
                </select>
                <button
                  className="btn"
                  disabled={refreshing}
                  onClick={toggleToday}
                  style={{
                    height: "38px",
                    backgroundColor: isTodayFilterActive ? "#bff1db" : "#fff",
                    border: isTodayFilterActive
                      ? "1px solid #9fd9bf"
                      : "1px solid #e5e1ef",
                    color: "#403b55",
                    fontWeight: "600",
                    fontSize: "13px",
                    padding: "0 14px",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      backgroundColor: isTodayFilterActive
                        ? "#355c4a"
                        : "#aaa5b3",
                      flexShrink: 0,
                    }}
                  />
                  {isTodayFilterActive
                    ? "Showing Today's Entries"
                    : "Show Today's Entries"}
                </button>
              </div>

              <div>
                <label
                  className="form-label mb-1"
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#514b65",
                    letterSpacing: "0.2px",
                  }}
                >
                  ENTRY DATE RANGE
                </label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    className="form-control"
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                    style={filterControlStyle}
                  />
                  <span style={{ color: "#77718b", fontSize: "12px" }}>to</span>
                  <input
                    className="form-control"
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                    style={filterControlStyle}
                  />
                  <button
                    className="btn"
                    disabled={
                      refreshing || !filters.fromDate || !filters.toDate
                    }
                    onClick={applyDateFilter}
                    style={{
                      height: "38px",
                      backgroundColor: "#fff",
                      color: "#514637",
                      border: "none",
                      fontWeight: "600",
                      fontSize: "12px",
                      padding: "0 15px",
                      borderRadius: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Apply Date Filter
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: "16px", flexShrink: 0 }} />

          {statementRows.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#fff",
                borderRadius: "14px",
                border: "1px solid #eeeaf8",
                boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                padding: "60px 20px",
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No account entries
              </div>
              <small style={{ marginTop: "5px" }}>
                Fee earnings, payouts, and adjustments will appear here.
              </small>
            </div>
          ) : (
            <>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #eeeaf8",
                  boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                }}
              >
                <div style={{ height: "100%", overflow: "auto" }}>
                  <table
                    className="table align-middle mb-0"
                    style={{
                      minWidth: "1180px",
                      width: "100%",
                      background: "#fff",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {[
                          "DATE",
                          "TYPE",
                          "DESCRIPTION",
                          "DIRECTION",
                          "OPENING",
                          "AMOUNT",
                          "CLOSING",
                          "FORM",
                          ...(canEdit ? ["ACTIONS"] : []),
                        ].map((heading) => (
                          <th
                            key={heading}
                            style={{
                              backgroundColor: "#d5d1fd",
                              color: "#403b55",
                              fontWeight: "600",
                              padding: "14px 16px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3eb",
                              whiteSpace: "nowrap",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                            }}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {statementRows.map((entry) => {
                        const isCredit = entry.direction === "credit";
                        return (
                          <tr key={entry.id}>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#403b55",
                                borderBottom: "1px solid #f0eef4",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDisplayDate(entry.entryDate)}
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                borderBottom: "1px solid #f0eef4",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  backgroundColor: "#ece8f7",
                                  color: "#403b55",
                                  borderRadius: "999px",
                                  padding: "3px 10px",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                }}
                              >
                                {entry.typeLabel || entryTypeLabel(entry.entryType)}
                              </span>
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#403b55",
                                borderBottom: "1px solid #f0eef4",
                                maxWidth: "260px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <AppTooltip
                                label={entry.description}
                                wrap
                                className="app-tip--stretch"
                              >
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {entry.description}
                                </span>
                              </AppTooltip>
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                fontWeight: "600",
                                color: isCredit ? "#2f6b4f" : "#8a3b3b",
                                borderBottom: "1px solid #f0eef4",
                              }}
                            >
                              {isCredit ? "Credit" : "Debit"}
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#625d75",
                                borderBottom: "1px solid #f0eef4",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatMoney(entry.openingBalance)}
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                fontWeight: "700",
                                color: isCredit ? "#2f6b4f" : "#8a3b3b",
                                borderBottom: "1px solid #f0eef4",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isCredit ? "+" : "−"}
                              {formatMoney(entry.amount)}
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#403b55",
                                borderBottom: "1px solid #f0eef4",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatMoney(entry.closingBalance)}
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                padding: "10px 16px",
                                borderBottom: "1px solid #f0eef4",
                                backgroundColor: "#fff",
                              }}
                            >
                              {isSafeFeeProofDisplayUrl(
                                entry.meta?.receivingFormImageUrl,
                              ) ? (
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() =>
                                    setViewingReceivingUrl(
                                      entry.meta.receivingFormImageUrl,
                                    )
                                  }
                                  style={{
                                    backgroundColor: "#ece8f7",
                                    border: "none",
                                    borderRadius: "8px",
                                    height: "34px",
                                    padding: "0 12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#403b55",
                                  }}
                                >
                                  View
                                </button>
                              ) : (
                                <span style={{ color: "#aaa5b5" }}>—</span>
                              )}
                            </td>
                            {canEdit ? (
                              <td
                                style={{
                                  textAlign: "center",
                                  padding: "10px 16px",
                                  borderBottom: "1px solid #f0eef4",
                                  backgroundColor: "#fff",
                                }}
                              >
                                {EDITABLE_TYPES.has(entry.entryType) ? (
                                  <AppTooltip label="Edit (reversal + new entry)">
                                  <button
                                    className="btn"
                                    onClick={() => openEdit(entry)}
                                    style={{
                                      backgroundColor: "#bff1db",
                                      border: "none",
                                      borderRadius: "8px",
                                      width: "38px",
                                      height: "34px",
                                      padding: 0,
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-pen"
                                      style={{
                                        fontSize: "14px",
                                        color: "#355c4a",
                                      }}
                                    />
                                  </button>
                                  </AppTooltip>
                                ) : (
                                  <span style={{ color: "#aaa5b5" }}>—</span>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <PaginationFooter
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="entries"
                disabled={refreshing}
              />
            </>
          )}
        </div>
      )}

      {showForm && (
        <div
          className="modal show"
          style={{ display: "block", backgroundColor: "rgba(48,45,69,0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                border: "none",
                borderRadius: "18px",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            >
              <div
                style={{
                  padding: "20px 22px",
                  backgroundColor: "#d5d1fd",
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5
                      className="mb-0"
                      style={{ color: "#302d45", fontWeight: "700" }}
                    >
                      {formMode === "edit"
                        ? "Edit entry"
                        : form.entryType === "payout"
                          ? "Record payout"
                          : "Add credit"}
                    </h5>
                    {formMode === "edit" ? (
                      <small style={{ color: "#625d75" }}>
                        Saves as a reversal of the original plus a new entry
                      </small>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeForm}
                  />
                </div>
              </div>
              <div style={{ padding: "22px" }}>
                <div className="mb-3">
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#403b55",
                    }}
                  >
                    Amount
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.amount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, amount: e.target.value }))
                    }
                    style={{
                      border: "1px solid #e5e1ef",
                      borderRadius: "9px",
                      padding: "10px 13px",
                    }}
                  />
                </div>
                <div className="mb-3">
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#403b55",
                    }}
                  >
                    Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.entryDate}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        entryDate: e.target.value,
                      }))
                    }
                    style={{
                      border: "1px solid #e5e1ef",
                      borderRadius: "9px",
                      padding: "10px 13px",
                    }}
                  />
                </div>
                <div className="mb-3">
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#403b55",
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    style={{
                      border: "1px solid #e5e1ef",
                      borderRadius: "9px",
                      padding: "10px 13px",
                    }}
                  />
                </div>

                {form.entryType === "payout" ||
                form.entryType === "manual_credit" ? (
                  <div
                    className="mb-3"
                    style={{
                      padding: "14px",
                      borderRadius: "12px",
                      backgroundColor: "#f7f5fc",
                      border: "1px solid #eeeaf8",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#403b55",
                        marginBottom: "6px",
                      }}
                    >
                      {form.entryType === "payout"
                        ? "Signed receiving form"
                        : "Signed credit confirmation form"}
                    </div>
                    <small
                      style={{
                        display: "block",
                        color: "#625d75",
                        marginBottom: "12px",
                        lineHeight: 1.45,
                      }}
                    >
                      Download the template, print it, get teacher and admin
                      signatures, then attach a photo of the signed form.
                    </small>
                    <button
                      type="button"
                      className="btn w-100 mb-3"
                      disabled={downloadingTemplate}
                      onClick={handleDownloadReceivingTemplate}
                      style={{
                        backgroundColor: "#fff",
                        color: "#403b55",
                        border: "1px solid #e5e1ef",
                        borderRadius: "9px",
                        padding: "10px",
                        fontWeight: "600",
                        fontSize: "13px",
                      }}
                    >
                      {downloadingTemplate
                        ? "Preparing…"
                        : form.entryType === "payout"
                          ? "Download receiving form template"
                          : "Download credit confirmation template"}
                    </button>

                    {formMode === "edit" &&
                    isSafeFeeProofDisplayUrl(
                      editingEntry?.meta?.receivingFormImageUrl,
                    ) ? (
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{
                          padding: "10px 12px",
                          borderRadius: "9px",
                          backgroundColor: "#fff",
                          border: "1px solid #e5e1ef",
                        }}
                      >
                        <span style={{ fontSize: "12px", color: "#625d75" }}>
                          Existing signed form will be kept
                        </span>
                        <button
                          type="button"
                          className="btn"
                          onClick={() =>
                            setViewingReceivingUrl(
                              editingEntry.meta.receivingFormImageUrl,
                            )
                          }
                          style={{
                            backgroundColor: "#ece8f7",
                            border: "none",
                            borderRadius: "8px",
                            height: "32px",
                            padding: "0 12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          View form
                        </button>
                      </div>
                    ) : formMode === "add" ? (
                      <>
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Attach signed form image{" "}
                          <span style={{ color: "#8a3b3b" }}>*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          accept={FEE_PROOF_ACCEPT}
                          onChange={handleReceivingFileChange}
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "8px 10px",
                            marginTop: "6px",
                          }}
                        />
                        {receivingFormFile ? (
                          <small
                            style={{
                              display: "block",
                              marginTop: "6px",
                              color: "#355c4a",
                            }}
                          >
                            Selected: {receivingFormFile.name}
                          </small>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}

                <button
                  className="btn w-100"
                  disabled={saving}
                  onClick={saveForm}
                  style={{
                    backgroundColor: "#bff1db",
                    color: "#fff",
                    border: "none",
                    borderRadius: "9px",
                    padding: "11px",
                    fontWeight: "600",
                  }}
                >
                  {saving
                    ? "Saving…"
                    : formMode === "edit"
                      ? "Save changes"
                      : "Save entry"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewingReceivingUrl ? (
        <div
          className="modal show"
          style={{ display: "block", backgroundColor: "rgba(48,45,69,0.55)" }}
          onClick={() => setViewingReceivingUrl("")}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{
                border: "none",
                borderRadius: "18px",
                overflow: "hidden",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  padding: "16px 20px",
                  backgroundColor: "#d5d1fd",
                }}
              >
                <h5
                  className="mb-0"
                  style={{ color: "#302d45", fontWeight: "700" }}
                >
                  Signed receiving form
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewingReceivingUrl("")}
                />
              </div>
              <div style={{ padding: "16px", backgroundColor: "#fff" }}>
                <img
                  src={viewingReceivingUrl}
                  alt="Signed receiving form"
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: "10px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
